import { Alert, Button, Stack } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { ApiError } from '~/api/client'
import { useT } from '~/i18n/use-t'

/** A message for an error: server wording is never shown raw, the member sees plain English. */
export function useFriendlyError() {
  const { t } = useT()
  return (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 429 || err.code === 'rate_limited') return t('errors.rate_limited')
      if (err.code === 'device_location_required') return t('errors.device_location_required')
      // A member with no stored face can only be verified by a person (spec §5.2).
      if (err.code === 'manual_route') return t('errors.manual_route')
      // The exemption no longer stood when the member pressed Continue; the step is re-read (face-first spec §3.4 B).
      if (err.code === 'document_exemption_stale') return t('errors.document_exemption_stale')
      return t('common.somethingWrong')
    }
    if (err instanceof TypeError) return t('errors.network')
    return t('common.somethingWrong')
  }
}

/** `id`: for the field the error is about (`aria-describedby`). Announced when it appears (`role='alert'`); `status` for a quiet note. */
export const ErrorAlert = ({
  children,
  id,
  role = 'alert',
}: {
  children: ReactNode
  id?: string
  role?: 'alert' | 'status'
}) => (
  <Alert.Root status='error' borderRadius='md' id={id} role={role}>
    <Alert.Indicator />
    <Alert.Content>
      <Alert.Description>{children}</Alert.Description>
    </Alert.Content>
  </Alert.Root>
)

/** A failed load with a way out: the friendly message and "Try again". */
export function RetryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  return (
    <Stack gap={3} align='flex-start'>
      <ErrorAlert>{friendly(error)}</ErrorAlert>
      <Button variant='outline' onClick={onRetry}>
        {t('common.tryAgain')}
      </Button>
    </Stack>
  )
}
